import { checkPreloadedBGM, CustomAction, RowType } from '@/components/Timeline/utils.ts';
import { useEffect, useMemo, useRef, useState } from 'react';
import { SCALE_WIDTH } from '@/components/Timeline/Timeline.tsx';
import { ActionEvent } from '@/components/Timeline/effects.ts';
import { formatFromHourToSecond } from '@/utils/dayjs';
import AudioWaveform from '@/components/Timeline/AudioWaveform';

interface CustomRenderActionProps {
  action: CustomAction;
  isSelected: boolean;
  isDragging: boolean;
  isResized: boolean;
  isVolumeChange: boolean;
  resizedDirection: string;
  timelineScale: number;
  disableResize: boolean;
  speakerColorList: { textColor: string; bgColor: string }[];
  onSelectedAction?: (action: CustomAction) => void;
  resetWaveform: number;
  handleDisableDrag: (state: boolean) => void;
  waveformCompleteWidth: number;
}

interface VttInfo {
  data: VttData | null;
  p: Promise<VttData> | null;
}

interface VttData {
  width: string;
  height: string;
  interval: number;
  zoom: number;
  imageUrl: string;
  list: [string, string][];
}

const vttInfoRecord: Record<string, VttInfo> = {};
function getVttInfo(url: string, text?: string): VttData | Promise<VttData> {
  const info = vttInfoRecord[url];
  if (!info) {
    vttInfoRecord[url] = {
      data: null,
      p: null,
    };
    if (text) {
      vttInfoRecord[url].data = analysisVtt(url, text);
      return vttInfoRecord[url].data;
    } else {
      vttInfoRecord[url].p = resolveVtt(url).then((data) => {
        vttInfoRecord[url].data = data;
        return data;
      });
      return vttInfoRecord[url].p;
    }
  } else if (!info.data) {
    return info.p!;
  }
  return info.data!;
}

function analysisVtt(url: string, text: string) {
  const spriteInfo: VttData = {
    width: '0',
    height: '0',
    zoom: 1,
    interval: 10000,
    list: [],
    imageUrl: url.replace(/vtt$/, 'jpg'),
  };
  const originSpriteArray = text.split('\n\n');
  originSpriteArray.shift();
  if (!originSpriteArray.at(-1)?.includes('xywh')) {
    originSpriteArray.pop();
  }
  const first = originSpriteArray[0];
  const xywh = first.split('xywh=')[1];
  const [x, y, w, h] = xywh.split(',');
  const zoom = 36 / Number(h);
  spriteInfo.width = w;
  spriteInfo.height = h;
  spriteInfo.zoom = zoom;
  spriteInfo.list.push([x, y]);
  const timeArray = first.split('\n')[0].split('-->');
  spriteInfo.interval = Number(timeArray[1].split(':')[2]);
  const restSpriteArray = originSpriteArray.slice(1);
  if (restSpriteArray.length > 0) {
    restSpriteArray.forEach((sprite) => {
      const xywh = sprite.split('xywh=')[1];
      const position = xywh.split(',');
      spriteInfo.list.push([position[0], position[1]]);
    });
  }
  return spriteInfo;
}

function getResource(url: string): Promise<string> {
  return new Promise((resolve) => {
    const xhr = new XMLHttpRequest();
    xhr.responseType = 'text';
    xhr.open('GET', url);
    xhr.onload = function () {
      if (xhr.readyState === xhr.DONE) {
        if (xhr.status === 200) {
          resolve(xhr.responseText);
        }
      }
    };
    xhr.send(null);
  });
}

function resolveVtt(url: string): Promise<VttData> {
  return getResource(url).then((text) => {
    return analysisVtt(url, text);
  });
}

function generateSpriteList(
  action: CustomAction,
  vttData: VttData,
  timelineScale: number,
  scaleWidth: number,
) {
  const spriteList: [string, string][] = [];
  const { width, interval, zoom, list } = vttData;
  if (!list || list.length === 0) {
    return spriteList;
  }
  const durationPerPixel = timelineScale / scaleWidth;
  const durationPerSprite = Number(width) * zoom * durationPerPixel;
  const { start, end, from } = action;
  let timePoint = from!;
  while (timePoint < end - start + from!) {
    const index = Math.floor(timePoint / interval);
    spriteList.push(list[index] || list.at(-1));
    timePoint += durationPerSprite;
  }
  return spriteList;
}

function CustomSubtitleAction({
  action,
  isSelected,
  isDragging,
  disableResize,
}: CustomRenderActionProps) {
  return (
    <div
      className={`custom-action custom-action-subtitle ${isSelected ? 'selected' : ''} ${isDragging ? 'is-dragging' : ''} ${disableResize ? 'disable-resize' : ''}`}>
      <div className="selected-bord"></div>
      <div className="text">{action.text}</div>
    </div>
  );
}

function CustomVideoAction({
  action,
  isSelected,
  isDragging,
  isResized,
  resizedDirection,
  timelineScale,
  disableResize,
}: CustomRenderActionProps) {
  const [duration, setDuration] = useState(0);
  const [vttData, setVttData] = useState<VttData | null>(null);
  useEffect(() => {
    const info = getVttInfo(action.vttUrl!, action.vttString);
    if (info instanceof Promise) {
      info.then(setVttData);
    } else {
      setVttData(info);
    }
  }, [action.vttUrl]);

  useEffect(() => {
    const duration = (action.end - action.start).toFixed(3);
    setDuration(+duration);
  }, [action.start, action.end]);

  const spriteContent = useMemo(() => {
    if (duration && vttData) {
      const { width, height, zoom, imageUrl } = vttData;
      const list = generateSpriteList(action, vttData, timelineScale, SCALE_WIDTH);
      return (
        <ul className="flex flex-nowrap">
          {list.map(([x, y], index) => (
            <li
              key={`${index}`}
              style={{
                minWidth: `${width}px`,
                height: `${height}px`,
                zoom: `${zoom}`,
                backgroundPosition: `${-x}px ${-y}px`,
                backgroundImage: `url("${imageUrl}")`,
              }}></li>
          ))}
        </ul>
      );
    }
  }, [duration, vttData, timelineScale]);

  return (
    <>
      {(isDragging || (isResized && resizedDirection === 'left')) && (
        <div
          className="tooltip tooltip-open"
          data-tip={formatFromHourToSecond(action.start * 1000)}
          style={{ position: 'absolute' }}
        />
      )}
      <div
        className={`custom-action custom-action-video ${isSelected ? 'selected' : ''}  ${isDragging ? 'is-dragging' : ''} ${disableResize ? 'disable-resize' : ''}`}>
        <div className="selected-bord"></div>
        {spriteContent}
      </div>
      {isResized && resizedDirection === 'right' && (
        <div
          className="tooltip tooltip-open"
          data-tip={formatFromHourToSecond(action.end * 1000)}
          style={{ position: 'absolute', right: 0, top: 0 }}
        />
      )}
    </>
  );
}

// function CustomMusicAction({
//   action,
//   isSelected,
//   isDragging,
//   timelineScale,
//   disableResize,
//   onSelectedAction,
//   resetWaveform,
//   handleDisableDrag,
// }: CustomRenderActionProps) {
//   const [waveStyle, setWaveStyle] = useState({});
//   const [waveVolume, setWaveVolume] = useState(action.volume! * 100);
//   const waveVolumRef = useRef<HTMLDivElement>(null);
//   const containerRef = useRef<HTMLDivElement>(null);
//   const bgmVolume = useRef<HTMLDivElement>(null);
//   const volumeInfo = useRef(0);
//   function calcWaveStyle(action: CustomAction) {
//     const { url, from } = action;
//     const imageInfo = getWaveImage(url!);
//     if (imageInfo) {
//       const { image, duration, imgw } = imageInfo;
//       const realWidth = duration / (timelineScale / SCALE_WIDTH);
//       const posX = (from! / duration) * realWidth;
//       return {
//         backgroundImage: `url("${image}")`,
//         backgroundPosition: `-${posX}px 0`,
//         backgroundSize: `${imgw}px 180%`,
//       };
//     }
//   }
//
//   useEffect(() => {
//     const style = calcWaveStyle(action);
//     if (style) {
//       setWaveStyle(style);
//     }
//   }, [action.url, action.from, timelineScale, resetWaveform]);
//
//   useEffect(() => {
//     setWaveVolume((action.volume || 0) * 100);
//   }, [action.volume]);
//   const handleMouseDown = (e: { preventDefault: () => void }) => {
//     e.preventDefault();
//     document.addEventListener('mousemove', handleMouseMove);
//     document.addEventListener('mouseup', handleMouseUp);
//   };
//   // 鼠标移动时触发
//   const handleMouseMove = (e: { clientX: number; clientY: number }) => {
//     onSelectedAction && onSelectedAction(action);
//     handleDisableDrag(true);
//     if (bgmVolume.current) bgmVolume.current.style.display = 'block';
//     const containerRect = containerRef.current?.getBoundingClientRect();
//     if (!containerRect) return;
//     // 获取鼠标在容器内的垂直位置
//     const mouseY = e.clientY - containerRect.top;
//     const mouseX = e.clientX - containerRect.left;
//     // 计算百分比
//     const percentage = (mouseY / containerRect.height) * 100;
//     // 限制百分比在 0 到 100 之间
//     const clampedPercentage = Math.min(Math.max(percentage, 0), 100);
//     const volume = Math.floor(100 - clampedPercentage);
//     setWaveVolume(volume);
//     volumeInfo.current = volume / 100;
//     if (bgmVolume.current) {
//       bgmVolume.current.style.left = `${mouseX}px`;
//       bgmVolume.current.style.bottom = `calc(${volume}% + 10px)`;
//     }
//   };
//   const handleMouseUp = () => {
//     handleDisableDrag(false);
//     action.volume = volumeInfo.current;
//     if (bgmVolume.current) bgmVolume.current.style.display = 'none';
//     ActionEvent.emit('changeVolume', action);
//     document.removeEventListener('mousemove', handleMouseMove);
//     document.removeEventListener('mouseup', handleMouseUp);
//   };
//   return (
//     <div
//       className={`custom-action custom-action-music ${isSelected ? 'selected' : ''}  ${isDragging ? 'is-dragging' : ''} ${disableResize ? 'disable-resize' : ''}`}>
//       <div className="selected-bord"></div>
//       <div className="wave" style={waveStyle}></div>
//       <div ref={containerRef} className="waveVolumeBlack">
//         <div
//           ref={waveVolumRef}
//           className="waveVolum"
//           onMouseDown={handleMouseDown}
//           style={{ bottom: `${waveVolume}%` }}></div>
//         <div className="bgmVolume" ref={bgmVolume}>
//           {waveVolume.toFixed(0)}
//         </div>
//       </div>
//     </div>
//   );
// }
function CustomMusicAction({
  action,
  isSelected,
  isDragging,
  timelineScale,
  disableResize,
  onSelectedAction,
  handleDisableDrag,
  waveformCompleteWidth,
}: CustomRenderActionProps) {
  const [waveVolume, setWaveVolume] = useState(action.volume! * 100);
  const waveVolumRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const bgmVolume = useRef<HTMLDivElement>(null);
  const volumeInfo = useRef(0);
  const [waveWidth, setWaveWidth] = useState(0);
  useEffect(() => {
    setWaveVolume((action.volume || 0) * 100);
  }, [action.volume]);
  useEffect(() => {
    const until = SCALE_WIDTH / timelineScale;
    const time = action.to! - action.from!;
    setWaveWidth(until * time);
  }, [timelineScale]);
  const handleMouseDown = (e: { preventDefault: () => void }) => {
    e.preventDefault();
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };
  // 鼠标移动时触发
  const handleMouseMove = (e: { clientX: number; clientY: number }) => {
    onSelectedAction && onSelectedAction(action);
    handleDisableDrag(true);
    if (bgmVolume.current) bgmVolume.current.style.display = 'block';
    const containerRect = containerRef.current?.getBoundingClientRect();
    if (!containerRect) return;
    // 获取鼠标在容器内的垂直位置
    const mouseY = e.clientY - containerRect.top;
    const mouseX = e.clientX - containerRect.left;
    // 计算百分比
    const percentage = (mouseY / containerRect.height) * 100;
    // 限制百分比在 0 到 100 之间
    const clampedPercentage = Math.min(Math.max(percentage, 0), 100);
    const volume = Math.floor(100 - clampedPercentage);
    setWaveVolume(volume);
    volumeInfo.current = volume / 100;
    if (bgmVolume.current) {
      bgmVolume.current.style.left = `${mouseX}px`;
      bgmVolume.current.style.bottom = `calc(${volume}% + 10px)`;
    }
  };
  const handleMouseUp = () => {
    handleDisableDrag(false);
    action.volume = volumeInfo.current;
    if (bgmVolume.current) bgmVolume.current.style.display = 'none';
    ActionEvent.emit('changeVolume', action);
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);
  };
  return (
    <div
      className={`custom-action custom-action-music ${isSelected ? 'selected' : ''}  ${isDragging ? 'is-dragging' : ''} ${disableResize ? 'disable-resize' : ''}`}>
      <div className="selected-bord"></div>
      <div style={{ height: '100%', width: '100%', overflow: 'hidden' }}>
        <AudioWaveform
          startTime={action.from}
          endTime={action.to}
          audioUrl={checkPreloadedBGM(action.url as string)}
          waveWidth={waveWidth}
          waveformCompleteWidth={waveformCompleteWidth}
        />
      </div>

      <div ref={containerRef} className="waveVolumeBlack">
        <div
          ref={waveVolumRef}
          className="waveVolum"
          onMouseDown={handleMouseDown}
          style={{ bottom: `${waveVolume}%` }}></div>
        <div className="bgmVolume" ref={bgmVolume}>
          {waveVolume.toFixed(0)}
        </div>
      </div>
    </div>
  );
}

function CustomAudioAction({
  action,
  isSelected,
  isDragging,
  disableResize,
  speakerColorList,
  isVolumeChange,
}: CustomRenderActionProps) {
  const speakerColor = {
    textColor: 'oklch(var(--p))',
    bgColor: 'rgba(203, 217, 255, 0.7)',
  };
  const selectedColor = speakerColorList[action.speakerId! - 1];
  if (selectedColor) {
    speakerColor.textColor = selectedColor.textColor;
    speakerColor.bgColor = selectedColor.bgColor;
  }
  return (
    <div
      className={`custom-action custom-action-audio ${isSelected ? 'selected' : ''}  ${isDragging ? 'is-dragging' : ''} ${disableResize ? 'disable-resize' : ''} ${isVolumeChange && 'change-volume-action'} ${action.isEdit! && action.text && 'speech-block-isEdit'} ${action.loading && 'speech-block-isEdit'}`}
      style={{
        backgroundColor: action.text ? speakerColor.bgColor : '#E6E6E6',
        color: action.text ? speakerColor.textColor : '#8D8D8D',
      }}>
      <div className="selected-bord"></div>
      {action.loading ? (
        <div className="audio-loading">
          <span
            className="loading loading-xs loading-spinner text-primary"
            style={{ color: '#FFFFFF' }}></span>
        </div>
      ) : (
        <div className="text">{action.text || `${(action.end - action.start).toFixed(1)}S`}</div>
      )}
    </div>
  );
}

export default function CustomRenderActions(props: CustomRenderActionProps) {
  const { action } = props;
  let actionEl = <CustomSubtitleAction {...props} />;
  switch (action.type) {
    case RowType.audio:
    case RowType.translation: {
      actionEl = <CustomAudioAction {...props} />;
      break;
    }
    case RowType.video: {
      actionEl = <CustomVideoAction {...props} />;
      break;
    }
    case RowType.music: {
      actionEl = <CustomMusicAction {...props} />;
      break;
    }
  }
  return actionEl;
}
